import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useI18n } from "@/context/TranslationContext";
import { MessagesSquare, TrendingUp, Mail, Send, Loader2 } from "lucide-react";
import { toast } from "sonner";

/** Passive risk radar: what leadership is asking the Ask ERIO copilot. */
export const TrendingQuestions = ({ canConfigure = false }) => {
  const { tx } = useI18n();
  const [data, setData] = useState(null);
  const [digest, setDigest] = useState(null);
  const [recips, setRecips] = useState("");
  const [savingD, setSavingD] = useState(false);
  const [testingD, setTestingD] = useState(false);

  useEffect(() => {
    api.get("/copilot/trending").then(({ data }) => setData(data)).catch(() => {});
    if (canConfigure) {
      api.get("/copilot/digest").then(({ data }) => {
        setDigest(data);
        setRecips((data.recipients || []).join(", "));
      }).catch(() => {});
    }
  }, [canConfigure]);

  const parseRecips = () => recips.split(/[,\n;]/).map((r) => r.trim()).filter(Boolean);

  const saveDigest = async (enabled) => {
    setSavingD(true);
    try {
      const { data } = await api.put("/copilot/digest", { enabled, recipients: parseRecips() });
      setDigest(data);
      toast.success(enabled ? "Weekly digest enabled." : "Digest settings saved.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not save.");
    } finally {
      setSavingD(false);
    }
  };

  const sendTestDigest = async () => {
    setTestingD(true);
    try {
      const body = parseRecips()[0] ? { recipient: parseRecips()[0] } : {};
      const { data } = await api.post("/copilot/digest/send-test", body);
      toast.success(`Digest sent (${data.sent} recipient(s), ${data.questions} answers).`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Test send failed.");
    } finally {
      setTestingD(false);
    }
  };

  if (!data || data.total_questions === 0) return null;

  return (
    <section className="mt-12" data-testid="trending-questions">
      <h2 className="flex items-center gap-2 ek-eyebrow">
        <MessagesSquare size={14} className="text-ek-purple" /> What leadership is asking
      </h2>
      <p className="mt-1 text-sm text-ek-ink/65">
        {data.total_questions} questions from {data.unique_askers} people to the Ask ERIO copilot
        in the last {data.window_days} days — a live read on executive concern.
      </p>

      <div className="mt-4 grid gap-6 lg:grid-cols-3">
        <div className="rounded-md border border-black/5 bg-white p-5 lg:col-span-2">
          <p className="text-xs font-semibold uppercase tracking-wide text-ek-ink/65">Most-asked questions</p>
          <ol className="mt-3 space-y-2" data-testid="trending-top-questions">
            {data.top_questions.map((q, i) => (
              <li key={i} className="flex items-start justify-between gap-3 text-sm">
                <span className="flex items-start gap-2 text-ek-ink/80">
                  <span className="mt-0.5 font-display text-xs font-bold text-ek-purple/50">{i + 1}</span>
                  {tx(q.question)}
                </span>
                <span className="shrink-0 rounded-full bg-ek-purple/10 px-2 py-0.5 text-xs font-bold text-ek-purple">
                  ×{q.count}
                </span>
              </li>
            ))}
          </ol>
        </div>

        <div className="rounded-md border border-black/5 bg-white p-5">
          <p className="flex items-center gap-1 text-xs font-semibold uppercase tracking-wide text-ek-ink/65">
            <TrendingUp size={12} /> Concern by theme
          </p>
          <div className="mt-3 space-y-2" data-testid="trending-themes">
            {data.themes.map((t) => {
              const max = data.themes[0]?.count || 1;
              return (
                <div key={t.theme}>
                  <div className="flex justify-between text-xs text-ek-ink/60">
                    <span>{t.theme}</span><span className="font-semibold">{t.count}</span>
                  </div>
                  <div className="mt-0.5 h-1.5 w-full overflow-hidden rounded-full bg-black/5">
                    <div className="h-full rounded-full bg-ek-purple" style={{ width: `${(t.count / max) * 100}%` }} />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {canConfigure && digest && (
        <div className="mt-4 flex flex-wrap items-center gap-3 rounded-md border border-ek-purple/20 bg-ek-purple/[0.04] p-4" data-testid="digest-config">
          <span className="flex items-center gap-1.5 text-sm font-semibold text-ek-ink">
            <Mail size={15} className="text-ek-purple" /> Weekly concern digest
          </span>
          <input
            type="text"
            value={recips}
            onChange={(e) => setRecips(e.target.value)}
            placeholder="cro@company.com, board@company.com"
            data-testid="digest-recipients"
            className="min-w-[220px] flex-1 rounded-sm border border-black/15 px-3 py-1.5 text-sm focus:border-ek-purple focus:outline-none"
          />
          <button
            onClick={sendTestDigest}
            disabled={testingD}
            data-testid="digest-send-test"
            className="flex items-center gap-1.5 rounded-sm border border-ek-purple px-3 py-1.5 text-sm font-semibold text-ek-purple hover:bg-ek-purple/5 disabled:opacity-50"
          >
            {testingD ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />} Send test
          </button>
          <button
            onClick={() => saveDigest(!digest.enabled)}
            disabled={savingD}
            data-testid="digest-toggle"
            className={`rounded-sm px-4 py-1.5 text-sm font-semibold text-white disabled:opacity-50 ${digest.enabled ? "bg-emerald-600" : "bg-ek-ink"}`}
          >
            {savingD ? "…" : digest.enabled ? "Enabled (Mondays) · click to pause" : "Enable weekly email"}
          </button>
        </div>
      )}
    </section>
  );
};
